// Amazon Connect sign-in from the logged-out screen (Storm 2026-10-21:
// "the double tab opening in Chrome when logging into the agent console").
//
// BEFORE: the button opened the full CCP page in our own popup. After sign-in
// that popup stayed open as a SECOND live CCP (a second softphone in the same
// browser) and the console waited for the agent to close it by hand, then
// reloaded the whole page.
//
// NOW: the button asks Streams to sign in (connect.core.authenticate, the
// library's own path, on the click so Chrome never blocks it). Streams opens
// a small login window, keeps refreshing the hidden CCP iframe until it is
// acknowledged, then CLOSES the window itself (autoClose). The console's
// agent subscription fires and the logged-out screen goes away: no second
// CCP left open, no page reload.
//
// Safety nets (any of these keeps today's behaviour):
//  - Streams' sign-in path missing or throws  -> our old popup + reload.
//  - The login window never opened (blocked)   -> our old popup + reload.
//  - The window closed but the agent is still not connected 8 s later
//    (e.g. the iframe ran out of refresh attempts) -> reload, as before.
//  - Once connected, any login window still open is closed by us.
// initCCP keeps loginPopup:false, so Streams never pops a window on its own
// at page load or on session expiry; only this click does.

export const LOGIN_OPTIONS = { autoClose: true, width: 400, height: 640 };
const POLL_MS = 500;
const BLOCKED_AFTER_MS = 3000;
const RELOAD_AFTER_CLOSE_MS = 8000;
const GIVE_UP_MS = 10 * 60 * 1000;
const REFRESH_LIMIT = 10;   // Streams CCP_IFRAME_REFRESH_LIMIT (2.25.1)

export function startConnectLogin({
  connectApi,
  ccpUrl,
  isConnected,
  openWindow = (...a) => window.open(...a),
  reloadPage = () => window.location.reload(),
  setIntervalFn = setInterval,
  clearIntervalFn = clearInterval,
  now = () => Date.now(),
}) {
  const fallback = () => {
    const popup = openWindow(ccpUrl, 'connectLogin', 'width=400,height=640,menubar=no,toolbar=no,location=no,status=no');
    const timer = setIntervalFn(() => {
      if (isConnected()) {
        clearIntervalFn(timer);
        try { if (popup && !popup.closed) popup.close(); } catch { /* already gone */ }
        return;
      }
      if (popup?.closed) {
        clearIntervalFn(timer);
        reloadPage();
      }
    }, POLL_MS);
    return 'fallback';
  };

  const core = connectApi?.core;
  const params = typeof connectApi === 'object' ? connectApi?.initCCPParams : null;
  if (!core || typeof core.authenticate !== 'function' || !params || !connectApi.containerDiv || !core.upstream) {
    return fallback();
  }
  // Streams only starts its iframe-refresh cycle when none is pending, and an
  // exhausted cycle (10 x 5 s, e.g. the one it ran at page load with no
  // session) leaves a stale handle behind. Clear it so the cycle restarts.
  const signIn = () => {
    try { clearTimeout(core.iframeRefreshTimeout); } catch { /* none */ }
    core.iframeRefreshTimeout = null;
    core.iframeRefreshAttempt = 0;
    core.authenticate(
      { ...params, loginPopup: true, loginPopupAutoClose: true, loginOptions: LOGIN_OPTIONS },
      connectApi.containerDiv,
      core.upstream,
    );
  };
  try {
    signIn();
  } catch {
    return fallback();
  }

  const startedAt = now();
  let sawWindow = false;
  let closedAt = null;
  const timer = setIntervalFn(() => {
    const win = core.loginWindow || null;
    if (isConnected()) {
      clearIntervalFn(timer);
      try { if (win && !win.closed) win.close(); } catch { /* already gone */ }
      return;
    }
    let open = false;
    try { open = Boolean(win && !win.closed); } catch { open = false; }
    if (open) {
      sawWindow = true; closedAt = null;
      // Still signing in after Streams ran out of refresh attempts: start
      // another cycle (the window is reused, not reopened).
      if ((core.iframeRefreshAttempt || 0) > REFRESH_LIMIT && now() - startedAt < GIVE_UP_MS) {
        try { signIn(); } catch { /* keep waiting; closing the window reloads */ }
      }
      return;
    }
    if (!sawWindow) {
      if (now() - startedAt >= BLOCKED_AFTER_MS) { clearIntervalFn(timer); fallback(); }
      return;
    }
    if (closedAt == null) closedAt = now();
    if (now() - closedAt >= RELOAD_AFTER_CLOSE_MS || now() - startedAt >= GIVE_UP_MS) {
      clearIntervalFn(timer);
      reloadPage();
    }
  }, POLL_MS);
  return 'streams';
}
